import { Footer } from "../../layouts/footer";
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
import axios from 'axios';

const AddProduct = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    bore: "",
    thick: "",
    thickness_tolerance: "",
    free_diameter: "",
    clearance_onbore: "",
    beam: "",
    lug: "",
    hole_diamter: "",
    ring_load_carrying_capacity: "",
    groove_diameter: "",
    groove_tolerance: "",
    groove_width: "",
    edge_margin: "",
    groove_load_capacity: "",
    catname: "",
    price_1_249: "",
    price_250_999: "",
    price_1000_4999: "",
    price_5000_9999: "",
    price_10000plus: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const optionalFields = [
      "price_1_249",
      "price_250_999",
      "price_1000_4999",
      "price_5000_9999",
      "price_10000plus"
    ];

    const cleanedFormData = Object.fromEntries(
      Object.entries(formData).filter(
        ([key, value]) =>
          value.trim() !== "" || !optionalFields.includes(key)
      )
    );

    axios.post(
      `${config.baseURL}/add_product`,
      cleanedFormData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then((response) => {
        if (response.data.status === 'success') {
          alert(response.data.message);
          navigate('/admin/products');
        } else {
          alert(response.data.message || 'Something went wrong.');
        }
      })
      .catch((error) => {
        console.error('❌ Error updating profile:', error);
        alert('Error occurred while updating profile.');
      });
  };

  useEffect(() => {
    setFormData(prev => ({ ...prev, catname: "20" }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-4xl bg-gray-800 rounded-lg shadow-xl overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-700">
          <p className="text-2xl font-bold text-white">Add New Product</p>
          <p className="mt-1 text-gray-400">Fill in the details below to add a new product to your inventory.</p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                Product Information
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Provide essential details about the product.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {/* Category Name - Made slightly more prominent as it's read-only */}
                <div className="sm:col-span-3">
                  <label
                    htmlFor="catname"
                    className="block text-sm font-medium leading-6 text-gray-300"
                  >
                    Product Category
                  </label>
                  <div className="mt-2">
                    <input
                      id="catname"
                      name="catname"
                      type="text"
                      value={formData.catname}
                      className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 cursor-not-allowed"
                      readOnly
                      disabled // Add disabled attribute for better UX on read-only fields
                    />
                  </div>
                </div>

                {/* Product Name */}
                <div className="sm:col-span-3">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium leading-6 text-gray-300"
                  >
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-2">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="e.g., Spring Washer M10"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                      required
                    />
                  </div>
                </div>

                {/* Other Input Fields - Replicated structure for consistency */}
                {[
                  { id: "bore", name: "bore", placeholder: "e.g., 10mm", label: "Bore" },
                  { id: "thick", name: "thick", placeholder: "e.g., 2.5mm", label: "Thick" },
                  { id: "thickness_tolerance", name: "thickness_tolerance", placeholder: "e.g., +/- 0.1mm", label: "Thickness Tolerance" },
                  { id: "free_diameter", name: "free_diameter", placeholder: "e.g., 18mm", label: "Free Diameter" },
                  { id: "clearance_onbore", name: "clearance_onbore", placeholder: "e.g., 0.5mm", label: "Clearance On Bore" },
                  { id: "beam", name: "beam", placeholder: "e.g., 5mm", label: "Beam" },
                  { id: "lug", name: "lug", placeholder: "e.g., 3mm", label: "Lug" },
                  { id: "hole_diamter", name: "hole_diamter", placeholder: "e.g., 10.2mm", label: "Hole Diameter" },
                  { id: "ring_load_carrying_capacity", name: "ring_load_carrying_capacity", placeholder: "e.g., 500 N", label: "Ring Load Carrying Capacity" },
                  { id: "groove_diameter", name: "groove_diameter", placeholder: "e.g., 15mm", label: "Groove Diameter" },
                  { id: "groove_tolerance", name: "groove_tolerance", placeholder: "e.g., +/- 0.05mm", label: "Groove Tolerance" },
                  { id: "groove_width", name: "groove_width", placeholder: "e.g., 3.2mm", label: "Groove Width" },
                  { id: "edge_margin", name: "edge_margin", placeholder: "e.g., 1.5mm", label: "Edge Margin" },
                  { id: "groove_load_capacity", name: "groove_load_capacity", placeholder: "e.g., 300 N", label: "Groove Load Capacity" },
                ].map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label}
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        placeholder={`Enter ${field.placeholder}`}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Price Information Section */}
            <div className="border-b border-gray-700 pb-8">
              <h3 className="text-xl font-semibold leading-7 text-white">
                Pricing
              </h3>
              <p className="mt-2 text-sm leading-6 text-gray-400">
                Set various pricing tiers for the product (optional).
              </p>
              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                {[
                  { id: "price_1_249", name: "price_1_249", label: "Price (1 to 249 units)", placeholder: "e.g., 0.50" },
                  { id: "price_250_999", name: "price_250_999", label: "Price (250 to 999 units)", placeholder: "e.g., 0.45" },
                  { id: "price_1000_4999", name: "price_1000_4999", label: "Price (1000 to 4999 units)", placeholder: "e.g., 0.40" },
                  { id: "price_5000_9999", name: "price_5000_9999", label: "Price (5000 to 9999 units)", placeholder: "e.g., 0.35" },
                  { id: "price_10000plus", name: "price_10000plus", label: "Price (10000+ units)", placeholder: "e.g., 0.30" },
                ].map((field) => (
                  <div className="sm:col-span-3" key={field.id}>
                    <label
                      htmlFor={field.id}
                      className="block text-sm font-medium leading-6 text-gray-300"
                    >
                      {field.label} ($)
                    </label>
                    <div className="mt-2">
                      <input
                        id={field.id}
                        name={field.name}
                        type="text"
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        className="block w-full rounded-md border-0 bg-gray-700 py-2 px-2 text-white shadow-sm ring-1 ring-inset ring-gray-600 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-5">
              <button
                type="submit"
                className="w-full rounded-md bg-blue-600 px-4 py-3 text-lg font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors duration-200"
              >
                Add Product
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AddProduct;